import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FilterFieldConfig } from '../filter-sidebar/filter-sidebar.component';

export interface FilterChip {
  fieldName: string;
  label: string;
  displayValue: string;
  /** Original value reference — used for item-level removal in multi-select fields */
  value?: any;
  /** Whether this chip's field is disabled (non-removable) */
  disabled?: boolean;
}

@Component({
  selector: 'app-filter-chips',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './filter-chips.component.html',
  styleUrls: ['./filter-chips.component.scss']
})
export class FilterChipsComponent implements OnChanges {
  /** Current filter values from the sidebar submit */
  @Input() activeFilters: Record<string, any> = {};
  /** Field configurations (for labels and type info) */
  @Input() fields: FilterFieldConfig[] = [];
  /** Emits field name + value when user removes a single chip */
  @Output() removeFilter = new EventEmitter<{ fieldName: string; value?: any }>();
  /** Emits when user clicks Clear All */
  @Output() clearAllFilters = new EventEmitter<void>();

  chips: FilterChip[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeFilters'] || changes['fields']) {
      this.buildChips();
    }
  }

  private buildChips(): void {
    this.chips = [];
    if (!this.activeFilters || !this.fields?.length) return;

    this.fields.forEach(field => {
      if (field.showChip === false) return;
      const value = this.activeFilters[field.name];
      if (value == null || value === '') return;

      // For arrays (multi-select), create a chip per selected item
      if (Array.isArray(value)) {
        value.forEach(item => {
          const display = this.resolveDisplayValue(field, item);
          if (display) {
            this.chips.push({
              fieldName: field.name,
              label: field.label,
              displayValue: display,
              value: item,
              disabled: field.disabled
            });
          }
        });
        return;
      }

      // For dropdown objects with { name, value }
      if (typeof value === 'object' && value.name) {
        this.chips.push({
          fieldName: field.name,
          label: field.label,
          displayValue: value.name,
          value,
          disabled: field.disabled
        });
        return;
      }

      if (field.type === 'dropdown' || field.type === 'multi-select') {
        const display = this.resolveDisplayValue(field, value);
        this.chips.push({
          fieldName: field.name,
          label: field.label,
          displayValue: display,
          value,
          disabled: field.disabled
        });
        return;
      }

      // For Date objects (calendar)
      if (value instanceof Date) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        this.chips.push({
          fieldName: field.name,
          label: field.label,
          displayValue: `${y}-${m}-${d}`,
          disabled: field.disabled
        });
        return;
      }

      // Plain string/number
      this.chips.push({
        fieldName: field.name,
        label: field.label,
        displayValue: String(value),
        value,
        disabled: field.disabled
      });
    });
  }

  private resolveDisplayValue(field: FilterFieldConfig, value: any): string {
    if (value == null || value === '') {
      return '';
    }

    if (typeof value === 'object' && value.name) {
      return value.name;
    }

    const optionValueKey = field.optionValue || 'value';
    const matched = field.options?.find(opt => {
      if (!opt || typeof opt !== 'object') {
        return false;
      }
      return opt[optionValueKey] === value || opt.value === value || opt.code === value;
    });

    return matched?.name ?? String(value);
  }

  onRemove(chip: FilterChip): void {
    this.removeFilter.emit({ fieldName: chip.fieldName, value: chip.value });
  }

  onClearAll(): void {
    this.clearAllFilters.emit();
  }
}
